import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import AppShell from "../../components/layout/AppShell";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { createBank } from "../../services/api";

// ─── Market config ────────────────────────────────────────────────────────────
const COUNTRY_LOCAL_CURRENCY = {
  US: "USD",
  AE: "AED",
  ET: "ETB",
  GH: "GHS",
  KE: "KES",
  MW: "MWK",
  MZ: "MZN",
  NG: "NGN",
  RW: "RWF",
  ZA: "ZAR",
  TZ: "TZS",
  UG: "UGX",
  ZM: "ZMW",
  ZW: "ZWL",
};

const COUNTRY_LABEL = {
  US: "United States",
  AE: "United Arab Emirates",
  ET: "Ethiopia",
  GH: "Ghana",
  KE: "Kenya",
  MW: "Malawi",
  MZ: "Mozambique",
  NG: "Nigeria",
  RW: "Rwanda",
  ZA: "South Africa",
  TZ: "Tanzania",
  UG: "Uganda",
  ZM: "Zambia",
  ZW: "Zimbabwe",
};

// ─── Component ────────────────────────────────────────────────────────────────
const AddBank = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);
  const authCountry = (useSelector((s) => s.auth.country) || "").toUpperCase();

  const localCurrency = COUNTRY_LOCAL_CURRENCY[authCountry] || null;

  // Currency options: USD always first; local added if different from USD
  const currencyOptions = localCurrency && localCurrency !== "USD"
    ? ["USD", localCurrency]
    : ["USD"];

  const [form, setForm] = useState({
    bankName:          "",
    accountHolderName: "",
    accountNumber:     "",
    routingNumber:     "",
    currency:          "USD",   // default USD
    streetLine:        "",
    city:              "",
    state:             "",
    postalCode:        "",
    isPrimary:         true,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (field, val) => {
    setForm((p) => ({ ...p, [field]: val }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.bankName.trim())            errs.bankName          = "Bank name is required.";
    if (!form.accountHolderName.trim())   errs.accountHolderName = "Account holder name is required.";
    if (!form.accountNumber.trim())       errs.accountNumber     = "Account number is required.";
    if (!form.routingNumber.trim())       errs.routingNumber     = "Routing number is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createBank({
        accountNumber:     form.accountNumber,
        accountHolderName: form.accountHolderName,
        userId,
        routingNumber:     form.routingNumber,
        thirdParty:        "Bridge",
        paymentMethod:     "ACH",
        region:            authCountry,
        bankName:          form.bankName,
        currency:          form.currency,
        streetLine:        form.streetLine,
        city:              form.city,
        state:             form.state,
        postalCode:        form.postalCode,
        country:           authCountry,
        isPrimary:         form.isPrimary,
      });
      toast.success("Bank account added successfully");
      navigate("/receiver/overview");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to add bank account. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Dynamic labels based on auth country
  const isAE = authCountry === "AE";
  const accountPlaceholder = isAE
    ? "Enter IBAN (e.g. AE070331234567890123456)"
    : "10-digit NUBAN account number";
  const routingLabel       = isAE ? "SWIFT / BIC Code" : "Routing Number";
  const routingPlaceholder = isAE ? "e.g. FABLAEADADX" : "Enter routing / branch code";

  return (
    <AppShell>
      <div className="max-w-[600px]">
        <h1 className="text-2xl font-bold text-textPrimary mb-1">Add Bank Account</h1>
        <p className="text-sm text-textMuted mb-6">
          Add your bank account to receive settlement payouts.
        </p>

        <div className="bg-cardBg border border-cardBorder rounded-card p-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">

              {/* ── Account Details ──────────────────────────────────────── */}
              <p className="text-xs font-semibold text-textMuted uppercase tracking-wider">
                Account details
              </p>

              <Input
                label="Bank Name"
                name="bankName"
                value={form.bankName}
                onChange={(e) => set("bankName", e.target.value)}
                placeholder="Enter bank name"
                error={errors.bankName}
                required
              />

              <Input
                label="Account Holder's Name"
                name="accountHolderName"
                value={form.accountHolderName}
                onChange={(e) => set("accountHolderName", e.target.value)}
                placeholder="Enter account holder name"
                error={errors.accountHolderName}
                required
              />

              <Input
                label="Account Number"
                name="accountNumber"
                value={form.accountNumber}
                onChange={(e) => set("accountNumber", e.target.value)}
                placeholder={accountPlaceholder}
                error={errors.accountNumber}
                required
              />

              <Input
                label={routingLabel}
                name="routingNumber"
                value={form.routingNumber}
                onChange={(e) => set("routingNumber", e.target.value)}
                placeholder={routingPlaceholder}
                error={errors.routingNumber}
                required
              />

              {/* Currency pill selector — only shown when a local option exists */}
              {currencyOptions.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Currency
                  </label>
                  <div className="flex gap-2">
                    {currencyOptions.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => set("currency", c)}
                        className={`px-5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          form.currency === c
                            ? "bg-brand text-white border-brand"
                            : "bg-white text-gray-600 border-gray-200 hover:border-brand hover:text-brand"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Bank Address Details ─────────────────────────────────── */}
              <p className="text-xs font-semibold text-textMuted uppercase tracking-wider pt-2">
                Bank Address details
              </p>

              <Input
                label="Street"
                name="streetLine"
                value={form.streetLine}
                onChange={(e) => set("streetLine", e.target.value)}
                placeholder="Enter street address"
              />

              <Input
                label="City"
                name="city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Enter city"
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="State"
                  name="state"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  placeholder="Enter state"
                />
                <Input
                  label="Pincode"
                  name="postalCode"
                  value={form.postalCode}
                  onChange={(e) => set("postalCode", e.target.value)}
                  placeholder="Enter pincode"
                />
              </div>

              {/* Country — read-only, derived from auth */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                <input
                  readOnly
                  value={COUNTRY_LABEL[authCountry] || authCountry || "—"}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>

              {/* ── Primary account toggle ───────────────────────────────── */}
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm font-medium text-gray-700">
                  Set as primary settlement account
                </span>
                <button
                  type="button"
                  onClick={() => set("isPrimary", !form.isPrimary)}
                  aria-checked={form.isPrimary}
                  role="switch"
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand/30 ${
                    form.isPrimary ? "bg-brand" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      form.isPrimary ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="mt-8">
              <Button type="submit" loading={submitting} fullWidth>
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AppShell>
  );
};

export default AddBank;
